<template>
  <transition :name="b('transition')">
    <button
      v-if="isPurechatAvailable"
      :class="b()"
      :aria-label="$t('c-chat-toggle.showChatWindow')"
      :disabled="!isReady"
      class="purechat-button-expand"
      type="button"
      @click="onClick"
    >
      <e-icon-sprite
        icon="i-chat"
        size="30"
      />
    </button>
  </transition>
</template>

<script>
  import useNotificationStore from '@/stores/notification';
  import eIconSprite from '@/elements/e-icon-sprite.vue';

  const purechat = 'purechatApi';
  const purechatWrapperId = 'PureChatWidget';
  const maxCheckIntervals = 2 * 60;
  const checkIntervalTimeout = 1_000;

  /**
   * Renders a toggle button for the chat service.
   */
  export default {
    name: 'c-chat-toggle',
    components: {
      eIconSprite,
    },

    // mixins: [],

    // props: {},
    setup() {
      return {
        notificationStore: useNotificationStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Boolean} Holds the availability state of purechat.
         */
        isPurechatAvailable: !!window[purechat],

        /**
         * @type {Number|null} Holds an interval while checking for the purechat instance.
         */
        availabilityInterval: null,

        /**
         * @type {Number} Holds an interval counter to cancel interval after a certain amount of loops.
         */
        intervalCounter: 0,

        /**
         * @type {Boolean} Becomes true when the purechat is fully ready.
         */
        isReady: false,
      };
    },

    // computed: {},
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      if (window[purechat]) {
        this.isPurechatAvailable = true;
        this.initializeChatbox();
      } else {
        this.startAvailabilityCheck();
      }
      this.$mitt.on('c-chat-toggle.openChat', this.onOpenChat);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      this.$mitt.off('c-chat-toggle.openChat', this.onOpenChat);
      this.stopAvailabilityCheck();
    },
    // unmounted() {},

    methods: {
      /**
       * Handles the click event of the button.
       *
       * Without this custom click handler (only using the purechat class), the chat box was not always shown.
       */
      onClick() {
        if (!this.isReady) {
          return;
        }

        try {
          this.showPurechatWindow();
          // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
        } catch (error) {
          this.notificationStore.showUnknownError();
        }
      },

      /**
       * Shows the purechat widget.
       *
       * We also have to set `chatbox.visible` to true,
       * because the purechat widget is completely hidden after loading the widget.
       *
       * Docs for `chatbox.expanded`: https://purechat.com/support/javascript-api#region=chatbox-expanded
       * Docs for `chatbox.visible`: https://purechat.com/support/javascript-api#region=chatbox-visible
       */
      showPurechatWindow() {
        if (window[purechat]) {
          window[purechat].set('chatbox.expanded', true);
          window[purechat].set('chatbox.visible', true);
        } else {
          console.warn('Trying to show unavailable purechat window.'); // eslint-disable-line no-console
        }
      },

      /**
       * Starts an interval to check the availability of purechat.
       */
      startAvailabilityCheck() {
        this.intervalCounter = 0;

        this.availabilityInterval = setInterval(() => {
          if (window[purechat]) {
            this.isPurechatAvailable = true;
            this.stopAvailabilityCheck();
            this.initializeChatbox();
          } else if (this.intervalCounter > maxCheckIntervals) {
            this.stopAvailabilityCheck();
          } else {
            this.intervalCounter += 1;
          }
        }, checkIntervalTimeout);
      },

      /**
       * Stops the purechat availability check.
       */
      stopAvailabilityCheck() {
        clearInterval(this.availabilityInterval);
      },

      /**
       * Event handler for listening to 'open' on the event bus.
       */
      onOpenChat() {
        this.showPurechatWindow();
      },

      /**
       * Initialize chatbox when ready.
       */
      initializeChatbox() {
        if (!window[purechat]) {
          // eslint-disable-next-line no-console
          console.warn('Trying to initialize unavailable purechat window.');

          this.notificationStore.showUnknownError();

          return;
        }

        // Hide immediatly if it is already shown
        if (document.getElementById(purechatWrapperId)) {
          window[purechat].set('chatbox.visible', false);
          this.isReady = true;
        }

        // Initially hide chatbox when ready
        window[purechat].on('chatbox:ready', () => {
          this.isReady = true;
          window[purechat].set('chatbox.visible', false);
        });
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  // @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-chat-toggle {
    $default-size: variables.$font-size--64;

    @extend %button-reset;
    @include mixins.font-size($default-size); // Button size.

    display: flex;
    justify-content: center;
    align-items: center;
    width: 1em;
    height: 1em;
    border-radius: 50%;
    background: linear-gradient(to top right, variables.$color-gradient--1);
    box-shadow: functions.rem(0 functions.calc-em(2px 6px, $default-size)) rgba(variables.$color-grayscale--0, 0.2);
    color: variables.$color-grayscale--1000;

    .e-icon-sprite {
      width: functions.calc-em(variables.$spacing--30, $default-size);
      height: functions.calc-em(variables.$spacing--30, $default-size);
    }

    &__transition-enter-active,
    &__transition-leave-active {
      transition:
        opacity variables.$transition-duration--300 linear variables.$transition-duration--100,
        width variables.$transition-duration--100;
    }

    &__transition-enter-from,
    &__transition-leave-to {
      width: 0;
      opacity: 0;
    }

    &:disabled {
      opacity: 0.3;
    }
  }
</style>
